import {useMemo} from 'react';
import type {RefObject} from 'react';
import type {DOMAttributes, FocusableElement, Key} from '@react-types/shared';
import type {MultiSelectState} from '../../@react-stately/select/useMultiSelectState';
import type {AriaListBoxOptions} from '../listbox';
import {setInteractionModality} from '../interactions';
import {useMenuTrigger} from '../menu';
import {useTypeSelect, ListKeyboardDelegate} from '../selection';
import {mapSelectionToKeysSet, mergeProps, useId} from '../utils';

export type SelectOptions = {
  isDisabled: boolean;
  /**
   * An optional keyboard delegate implementation for type to select,
   * to override the default.
   */
  keyboardDelegate?: ListKeyboardDelegate<unknown>;

  onBlur?: DOMAttributes<Element>['onBlur'];
};

export interface SelectAria {
  /** Props for the label element. */
  labelProps: DOMAttributes;

  /** Props for the popup trigger element. */
  triggerProps: DOMAttributes<Element>;

  /** Props for the element representing the selected value. */
  valueProps: DOMAttributes;

  /** Props for the popup. */
  listboxProps: AriaListBoxOptions<unknown> & {autoFocus: boolean};
}

/**
 * A replacement for
 * [useSelect](https://react-spectrum.adobe.com/react-aria/useSelect.html)
 * Provides the behavior and accessibility implementation for a select component.
 * A select displays a collapsible list of options and allows a user to select one of them.
 * @param props - Props for the select.
 * @param state - State for the select, as returned by `useListState`.
 */
export function useSelect<T>(
  props: SelectOptions,
  state: MultiSelectState,
  ref: RefObject<FocusableElement>,
): SelectAria {
  const {keyboardDelegate, isDisabled} = props;

  // By default, a KeyboardDelegate is provided which uses the DOM to query layout information (e.g. for page up/page down).
  // When virtualized, the layout object will be passed in as a prop and override this.
  const delegate = useMemo(
    () =>
      keyboardDelegate ||
      new ListKeyboardDelegate(
        state.collection,
        state.disabledKeys,
        // This is a strange react-aria pattern: they make this prop required,
        // but our TS config is stricter so we have to pass something. This
        // delegate is not passed around, but only used this this file so we
        // only call a few functions on it which don't require the ref. This ref
        // should be the listbox ref but we don't pass it in yet.
        {current: null},
      ),
    [keyboardDelegate, state.collection, state.disabledKeys],
  );

  const {menuTriggerProps, menuProps} = useMenuTrigger<T>(
    {
      isDisabled,
      type: 'listbox',
    },
    state,
    ref,
  );

  const getFirstKey = (keys: Iterable<Key> | undefined): Key | null => {
    if (keys === undefined) {
      return null;
    }
    const keysArray: Key[] = [...mapSelectionToKeysSet(keys)];
    return keysArray?.[0] ?? null;
  };

  const onKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft': {
        // prevent scrolling containers
        e.preventDefault();

        if (state.selectionManager.selectionMode === 'single') {
          const selectedKey = getFirstKey(state.selectedKeys);
          const key =
            selectedKey != null
              ? delegate.getKeyAbove(selectedKey)
              : delegate.getFirstKey();
          if (key) {
            state.setSelectedKeys(new Set([key]));
          }
        }
        break;
      }
      case 'ArrowRight': {
        // prevent scrolling containers
        e.preventDefault();

        if (state.selectionManager.selectionMode === 'single') {
          const selectedKey = getFirstKey(state.selectedKeys);
          const key =
            selectedKey != null
              ? delegate.getKeyBelow(selectedKey)
              : delegate.getFirstKey();
          if (key) {
            state.setSelectedKeys(new Set([key]));
          }
        }
        break;
      }
      default:
    }
  };

  const {typeSelectProps} = useTypeSelect({
    keyboardDelegate: delegate,
    selectionManager: state.selectionManager,
    onTypeSelect(key) {
      if (state.selectionManager.selectionMode === 'single') {
        state.setSelectedKeys(new Set([key]));
      }
    },
  });

  typeSelectProps.onKeyDown = typeSelectProps.onKeyDownCapture;
  delete typeSelectProps.onKeyDownCapture;

  const valueId = useId();

  // TODO: handle `aria-labelledby` and `aria-label`. We removed it because we
  // don't do as much prop processing, but see here:
  // https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/select/src/useSelect.ts#L143
  return {
    labelProps: {
      onClick: () => {
        if (!isDisabled) {
          ref.current?.focus();

          // Show the focus ring so the user knows where focus went
          setInteractionModality('keyboard');
        }
      },
    },
    triggerProps: mergeProps(typeSelectProps, menuTriggerProps, {
      disabled: isDisabled,
      'aria-haspopup': '',
      onKeyDown,
      onFocus(_: FocusEvent) {
        state.setFocused(true);
      },
      onBlur(_: FocusEvent) {
        state.setFocused(false);
      },
    }),
    valueProps: {
      id: valueId,
    },
    listboxProps: {
      ...menuProps,
      autoFocus: true,
      shouldSelectOnPressUp: true,
      shouldFocusOnHover: true,
      disallowEmptySelection: true,
      linkBehavior: 'selection',
      onBlur: (e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) {
          return;
        }

        if (props.onBlur) {
          props.onBlur(e);
        }

        state.setFocused(false);
      },
    },
  };
}
